import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { FIELD_META, ROLES } from "../data/content";
import { submitLead } from "../lib/api";
import { PresentationVideo } from "./PresentationVideo";

export const LeadModal = ({ role, open, onOpenChange, referral, invite }) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (open) {
      setDone(false);
      setForm({});
      setConsent(false);
    }
  }, [open]);

  const config = role ? ROLES[role] : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const field of config.fields) {
      const meta = FIELD_META[field];
      if (meta.required && !String(form[field] || "").trim()) {
        toast.error(`Preencha o campo: ${meta.label}`);
        return;
      }
    }
    setLoading(true);
    if (!consent) {
      setLoading(false);
      toast.error("É necessário declarar ciência dos Termos de Uso e da Política de Privacidade para enviar.");
      return;
    }
    try {
      const payload = { role, consent: true };
      config.fields.forEach((f) => {
        if (form[f]) payload[f] = form[f];
      });
      if (referral) payload.referral_code = referral;
      if (role === "EMBAIXADORA" && invite) {
        payload.preferred_miss_aura_code = invite.code;
        payload.miss_aura_name = invite.name;
      }
      await submitLead(payload);
      setDone(true);
      toast.success("Recebido com amor. Bem-vinda ao AURA.");
    } catch {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={role ? `lead-modal-${role.toLowerCase()}` : "lead-modal"}
        className="max-w-md border-gold-border bg-cream sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        {config && !done && (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl text-ink">
                {config.title}
              </DialogTitle>
              <DialogDescription className="text-sm text-mutedink">
                {config.tagline}
              </DialogDescription>
            </DialogHeader>
            {referral && (
              <div
                data-testid="referral-badge"
                className="mt-4 inline-flex w-fit rounded-full bg-teal-light px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-teal-deep"
              >
                Indicação de {referral}
              </div>
            )}
            {role === "EMBAIXADORA" && invite && (
              <div
                data-testid="miss-aura-invite-banner"
                className="mt-4 rounded-2xl border border-gold-border bg-gold-light px-5 py-4"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold">
                  Convite reconhecido
                </p>
                <p className="mt-1 text-sm leading-relaxed text-ink">
                  Candidatura vinculada à Miss Aura{" "}
                  <span className="font-semibold">{invite.name}</span>.
                </p>
              </div>
            )}
            <PresentationVideo />
            <form
              data-testid={`lead-form-${role}`}
              onSubmit={handleSubmit}
              className="mt-6 space-y-5"
            >
              {config.fields.map((field) => {
                const meta = FIELD_META[field];
                return (
                  <div key={field} className="space-y-2">
                    <Label
                      htmlFor={`lead-${field}`}
                      className="text-xs font-semibold uppercase tracking-[0.15em] text-mutedink"
                    >
                      {meta.label}
                      {meta.required && " *"}
                    </Label>
                    {meta.textarea ? (
                      <Textarea
                        id={`lead-${field}`}
                        data-testid={`lead-input-${field}`}
                        placeholder={meta.placeholder}
                        value={form[field] || ""}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, [field]: e.target.value }))
                        }
                        className="min-h-[110px] border-gold-border bg-white"
                      />
                    ) : (
                      <Input
                        id={`lead-${field}`}
                        data-testid={`lead-input-${field}`}
                        type={meta.type || "text"}
                        placeholder={meta.placeholder}
                        value={form[field] || ""}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, [field]: e.target.value }))
                        }
                        className="border-gold-border bg-white"
                      />
                    )}
                  </div>
                );
              })}
              <div className="rounded-2xl border border-gold-border bg-white p-4">
                <label
                  data-testid="lead-consent-label"
                  className="flex cursor-pointer items-start gap-3"
                >
                  <input
                    type="checkbox"
                    data-testid="lead-consent-checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-teal"
                  />
                  <span className="text-xs leading-relaxed text-mutedink">
                    Declaro que li e estou ciente dos{" "}
                    <a
                      href="/termos-de-uso"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="lead-consent-terms-link"
                      className="font-semibold text-teal underline underline-offset-2 hover:text-teal-hover"
                    >
                      Termos de Uso
                    </a>{" "}
                    e da{" "}
                    <a
                      href="/politica-de-privacidade"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="lead-consent-privacy-link"
                      className="font-semibold text-teal underline underline-offset-2 hover:text-teal-hover"
                    >
                      Política de Privacidade
                    </a>{" "}
                    do Ministério AURA e autorizo o tratamento dos meus dados
                    pessoais para as finalidades relacionadas à minha
                    candidatura e participação no processo de seleção, conforme
                    descrito na Política de Privacidade.
                  </span>
                </label>
              </div>
              <Button
                data-testid="lead-form-submit-button"
                type="submit"
                disabled={loading || !consent}
                className="w-full rounded-full bg-teal py-6 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-teal-hover disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Enviar minha inscrição"
                )}
              </Button>
            </form>
          </>
        )}

        {config && done && (
          <div data-testid="lead-success" className="py-10 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-teal" />
            <h3 className="mt-6 font-serif text-3xl text-ink">
              Bem-vinda ao AURA.
            </h3>
            <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-mutedink">
              Nossa equipe entrará em contato com você em breve. Guarde este
              momento: ele é o começo de algo grande.
            </p>
            <Button
              data-testid="lead-success-close-btn"
              onClick={() => onOpenChange(false)}
              className="mt-8 rounded-full bg-teal px-10 py-5 text-xs font-semibold uppercase tracking-[0.2em] text-white hover:bg-teal-hover"
            >
              Fechar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
